import React, { useEffect, useRef, useState, useCallback } from 'react';
import { fetchWrapper } from 'utils/apiFetch';
import { useSession } from 'hooks/useSession';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt  = (n) => '₹\u00a0' + (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtN = (n) => (parseInt(n)  || 0).toLocaleString('en-IN');
const BASE  = 'https://pro.mokshiq.in';
const AUTH  = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// ─── Service palette ──────────────────────────────────────────────────────────
const SVC = {
  recharge: { label: 'Mobile Recharge', icon: '📱', color: '#1d9e75', grad: 'linear-gradient(135deg,#0f6e56,#1d9e75)', accent: '#5dcaa5', bg: '#e1f5ee', product: ['recharge'] },
  dth:      { label: 'DTH Recharge',    icon: '📡', color: '#185fa5', grad: 'linear-gradient(135deg,#0c447c,#185fa5)', accent: '#85b7eb', bg: '#e6f1fb', product: ['dth']      },
  bbps:     { label: 'Bill Payments',   icon: '🏦', color: '#ba7517', grad: 'linear-gradient(135deg,#854f0b,#ba7517)', accent: '#fac775', bg: '#faeeda', product: ['bbps']     },
  offline:  { label: 'Offline BBPS',    icon: '🏪', color: '#534ab7', grad: 'linear-gradient(135deg,#3c3489,#534ab7)', accent: '#afa9ec', bg: '#eeedfe', product: ['bbps_offline','offline_bbps'] },
};

// ─── Status badge ─────────────────────────────────────────────────────────────
const statusStyle = (s = '') => {
  const l = s.toLowerCase();
  if (l.includes('success')) return { background: '#e1f5ee', color: '#0f6e56', border: '1px solid #5dcaa522' };
  if (l.includes('fail'))    return { background: '#fcebeb', color: '#791f1f', border: '1px solid #f0959522' };
  if (l.includes('pend'))    return { background: '#faeeda', color: '#633806', border: '1px solid #fac77522' };
  return                            { background: '#f0f0f0', color: '#555',    border: '1px solid #ddd' };
};

// ─── CSS ──────────────────────────────────────────────────────────────────────
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
.ap-root *, .ap-root *::before, .ap-root *::after { box-sizing: border-box; margin: 0; padding: 0; }
.ap-root { font-family: 'Plus Jakarta Sans', sans-serif; background: #f0f2f5; min-height: 100vh; padding: 20px 18px; }
@keyframes apFadeUp  { from { opacity:0; transform:translateY(14px) } to { opacity:1; transform:translateY(0) } }
@keyframes apSlideIn { from { opacity:0; transform:translateX(-8px) } to { opacity:1; transform:translateX(0) } }
@keyframes apPulse   { 0%,100%{opacity:1} 50%{opacity:.3} }
@keyframes apShimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
@keyframes apSpin    { to { transform:rotate(360deg) } }
@keyframes apBarGrow { from{width:0} to{width:var(--w)} }
.ap-fade   { animation: apFadeUp  .45s cubic-bezier(.22,1,.36,1) both }
.ap-slide  { animation: apSlideIn .35s ease both }
.ap-spin   { display:inline-block; animation: apSpin .7s linear infinite }

/* Cards */
.ap-svc-card { border-radius:18px; padding:22px; position:relative; overflow:hidden; cursor:pointer;
  box-shadow:0 4px 20px rgba(0,0,0,.12); transition:transform .22s,box-shadow .22s; }
.ap-svc-card:hover { transform:translateY(-4px) scale(1.015); box-shadow:0 12px 32px rgba(0,0,0,.2); }
.ap-svc-card::before { content:''; position:absolute; top:-28px; right:-28px; width:110px; height:110px;
  border-radius:50%; background:rgba(255,255,255,.1); }
.ap-svc-card::after  { content:''; position:absolute; bottom:-18px; left:-18px; width:75px; height:75px;
  border-radius:50%; background:rgba(255,255,255,.06); }

/* Panel */
.ap-panel { background:#fff; border-radius:16px; box-shadow:0 2px 14px rgba(0,0,0,.06);
  overflow:hidden; transition:box-shadow .2s; }
.ap-panel:hover { box-shadow:0 6px 24px rgba(0,0,0,.1); }

/* Tabs */
.ap-tab-bar { display:flex; gap:4px; background:#fff; border-radius:14px; padding:5px;
  box-shadow:0 2px 12px rgba(0,0,0,.07); margin-bottom:20px; overflow-x:auto; scrollbar-width:none; }
.ap-tab-bar::-webkit-scrollbar { display:none; }
.ap-tab-btn { display:flex; align-items:center; gap:6px; padding:8px 16px; border-radius:10px; border:none;
  background:transparent; cursor:pointer; font-family:inherit; font-size:12px; font-weight:600; color:#888;
  white-space:nowrap; flex-shrink:0; transition:all .2s; }
.ap-tab-btn:hover:not(.active) { background:#f5f6f8; color:#444; }
.ap-tab-btn.active { color:#fff; box-shadow:0 4px 12px rgba(0,0,0,.25); }

/* Table */
.ap-table { width:100%; border-collapse:collapse; font-size:12px; }
.ap-table th { padding:11px 14px; text-align:left; font-size:9px; font-weight:700; letter-spacing:.12em;
  text-transform:uppercase; color:#777; white-space:nowrap; background:linear-gradient(90deg,#f7f8fa,#eef0f3);
  border-bottom:1px solid rgba(0,0,0,.07); position:sticky; top:0; z-index:2; }
.ap-table td { padding:11px 14px; color:#1a1a1a; font-family:'JetBrains Mono',monospace; font-size:11.5px;
  font-weight:500; white-space:nowrap; border-bottom:.5px solid rgba(0,0,0,.05); }
.ap-table tr:hover td { background:#f7f8fa; }
.ap-table tr:last-child td { border-bottom:none; }

/* Key box */
.ap-key-box { background:#0f1117; border-radius:12px; padding:14px 18px; display:flex; align-items:center;
  gap:10px; border:1px solid rgba(255,255,255,.1); }
.ap-key-text { font-family:'JetBrains Mono',monospace; font-size:13px; color:#5dcaa5; flex:1;
  word-break:break-all; letter-spacing:.04em; }

/* Buttons */
.ap-btn { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border-radius:10px;
  font-family:inherit; font-size:12px; font-weight:700; cursor:pointer; border:none; transition:all .18s; }
.ap-btn:disabled { opacity:.55; cursor:not-allowed; }
.ap-btn-violet  { background:linear-gradient(135deg,#3c3489,#534ab7); color:#fff;
  box-shadow:0 4px 14px rgba(83,74,183,.35); }
.ap-btn-violet:hover:not(:disabled)  { transform:translateY(-1px); box-shadow:0 6px 20px rgba(83,74,183,.45); }
.ap-btn-red     { background:linear-gradient(135deg,#791f1f,#a32d2d); color:#fff;
  box-shadow:0 4px 14px rgba(163,45,45,.3); }
.ap-btn-red:hover:not(:disabled)     { transform:translateY(-1px); }
.ap-btn-green   { background:linear-gradient(135deg,#0f6e56,#1d9e75); color:#fff;
  box-shadow:0 4px 14px rgba(29,158,117,.3); }
.ap-btn-green:hover:not(:disabled)   { transform:translateY(-1px); }
.ap-btn-ghost   { background:#fff; color:#534ab7; border:1.5px solid #534ab7 !important;
  box-shadow:0 2px 8px rgba(0,0,0,.06); }
.ap-btn-ghost:hover:not(:disabled)   { background:#f0efff; }
.ap-btn-sm { padding:6px 13px; font-size:11px; }

/* Input */
.ap-input { background:#f7f8fa; border:1.5px solid #e5e7eb; border-radius:10px; padding:9px 13px;
  font-family:'JetBrains Mono',monospace; font-size:13px; color:#1a1a1a; outline:none;
  transition:border-color .2s; width:100%; }
.ap-input:focus { border-color:#534ab7; background:#fff; }

/* Section head */
.ap-sh { display:flex; align-items:center; gap:9px; margin-bottom:13px; }
.ap-sh-bar { width:4px; height:15px; border-radius:2px; flex-shrink:0; }
.ap-sh-title { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.12em; color:#888; }
.ap-sh-line  { flex:1; height:.5px; background:rgba(0,0,0,.07); }

/* Bar fill */
.ap-bar { height:6px; background:#f0f0f0; border-radius:999px; overflow:hidden; margin-top:6px; }
.ap-bar-fill { height:100%; border-radius:999px; animation:apBarGrow .8s cubic-bezier(.22,1,.36,1) both;
  animation-delay:var(--d,0s); width:var(--w,0%); }

/* Dot */
.ap-dot-on  { width:8px; height:8px; border-radius:50%; background:#1d9e75; flex-shrink:0;
  box-shadow:0 0 0 3px rgba(29,158,117,.25); animation:apPulse 2s infinite; }
.ap-dot-off { width:8px; height:8px; border-radius:50%; background:#a32d2d; flex-shrink:0;
  box-shadow:0 0 0 3px rgba(163,45,45,.25); }

/* Toast */
.ap-toast { position:fixed; bottom:24px; right:24px; background:#1a1a1a; color:#fff; padding:12px 20px;
  border-radius:12px; font-size:13px; font-weight:600; box-shadow:0 8px 28px rgba(0,0,0,.25); z-index:9999;
  display:flex; align-items:center; gap:8px; animation:apFadeUp .3s ease both; }

/* Live dot */
.ap-live { width:7px; height:7px; border-radius:50%; background:#1d9e75;
  animation:apPulse 2s infinite; box-shadow:0 0 0 3px rgba(29,158,117,.2); }

/* Shimmer bar */
.ap-shimmer { position:absolute; bottom:0; left:0; right:0; height:2px;
  background:linear-gradient(90deg,transparent,var(--accent),transparent);
  background-size:200% 100%; animation:apShimmer 2.5s linear infinite; }
`;

// ─── Small shared components ──────────────────────────────────────────────────
function SH({ color = '#534ab7', children, delay = 0 }) {
  return (
    <div className="ap-sh ap-fade" style={{ animationDelay: `${delay}s` }}>
      <div className="ap-sh-bar" style={{ background: color }} />
      <span className="ap-sh-title">{children}</span>
      <div className="ap-sh-line" />
    </div>
  );
}

function Toast({ msg, icon = '✅', onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2600); return () => clearTimeout(t); }, [onDone]);
  return <div className="ap-toast"><span>{icon}</span>{msg}</div>;
}

function StatMini({ label, value, sub, color = '#534ab7' }) {
  return (
    <div style={{ padding: '14px 16px', background: '#fff', borderRadius: 14,
      boxShadow: '0 2px 10px rgba(0,0,0,.06)', borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: 10, color: '#aaa', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '.08em', marginBottom: 5 }}>{label}</div>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 18, fontWeight: 700,
        color, marginBottom: sub ? 3 : 0 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#aaa' }}>{sub}</div>}
    </div>
  );
}

// ─── Service Card ─────────────────────────────────────────────────────────────
function ServiceCard({ svcKey, data, active, onClick, delay }) {
  const s    = SVC[svcKey];
  const succ = data?.success_count ?? 0;
  const fail = data?.failed_count  ?? 0;
  const total = succ + fail;
  const pct  = total ? ((succ / total) * 100).toFixed(0) : 0;

  return (
    <div className={`ap-svc-card ap-fade`}
      style={{ background: s.grad, animationDelay: `${delay}s`,
        outline: active ? '2.5px solid rgba(255,255,255,.6)' : 'none',
        outlineOffset: 2 }}
      onClick={onClick}
    >
      <div style={{ '--accent': s.accent }} className="ap-shimmer" />
      <div style={{ position: 'absolute', top: 14, right: 16, fontSize: 26,
        color: 'rgba(255,255,255,.2)' }}>{s.icon}</div>

      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '.1em', color: 'rgba(255,255,255,.6)', marginBottom: 8,
        position: 'relative', zIndex: 1 }}>{s.label}</div>

      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 20, fontWeight: 600,
        color: '#fff', marginBottom: 4, position: 'relative', zIndex: 1 }}>
        {fmtN(succ)} <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,.6)' }}>success</span>
      </div>

      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 13, color: s.accent,
        marginBottom: 12, position: 'relative', zIndex: 1 }}>{fmt(data?.success_amount ?? 0)}</div>

      {/* success rate bar */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,.55)' }}>Success rate</span>
          <span style={{ fontSize: 10, color: '#fff', fontWeight: 700 }}>{pct}%</span>
        </div>
        <div style={{ height: 5, background: 'rgba(255,255,255,.18)', borderRadius: 999, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: s.accent,
            borderRadius: 999, transition: 'width .7s' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 12, position: 'relative', zIndex: 1 }}>
        <div style={{ flex: 1, background: 'rgba(255,255,255,.13)', borderRadius: 9, padding: '8px 10px',
          border: '1px solid rgba(255,255,255,.15)' }}>
          <div style={{ fontSize: 8, color: 'rgba(255,255,255,.55)', marginBottom: 3 }}>FAILED</div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 14, color: '#f7c1c1',
            fontWeight: 600 }}>{fmtN(fail)}</div>
        </div>
        <div style={{ flex: 1, background: 'rgba(255,255,255,.13)', borderRadius: 9, padding: '8px 10px',
          border: '1px solid rgba(255,255,255,.15)' }}>
          <div style={{ fontSize: 8, color: 'rgba(255,255,255,.55)', marginBottom: 3 }}>TOTAL TXN</div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 14, color: '#fff',
            fontWeight: 600 }}>{fmtN(total)}</div>
        </div>
      </div>

      {active && (
        <div style={{ position: 'absolute', bottom: 10, right: 12, fontSize: 10, color: 'rgba(255,255,255,.7)',
          fontWeight: 700, background: 'rgba(255,255,255,.2)', padding: '3px 10px', borderRadius: 999 }}>
          Viewing ↓
        </div>
      )}
    </div>
  );
}

// ─── Reports Table ────────────────────────────────────────────────────────────
function ReportsTable({ userid, svcKey, dateFrom, dateTo }) {
  const s       = SVC[svcKey];
  const [rows,  setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [page,  setPage]    = useState(1);
  const PER = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Map each service key to the real existing endpoint + params
      const endpointMap = {
        recharge: { url: `${BASE}/get_recharge_report`, extra: { product: 'recharge' } },
        dth:      { url: `${BASE}/get_recharge_report`, extra: { product: 'dth'      } },
        bbps:     { url: `${BASE}/bbps_report_data`,    extra: {}                       },
        offline:  { url: `${BASE}/get_offline_bbps_report`, extra: {}                  },
      };
      const ep = endpointMap[svcKey] || endpointMap.recharge;

      const params = new URLSearchParams({
        id:         userid,
        start_date: dateFrom,
        end_date:   dateTo,
        ...ep.extra,
      });
      const r = await fetch(`${ep.url}?${params}`, { headers: AUTH() });
      const d = await r.json();
      setRows(d.data || []);
      setPage(1);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [userid, svcKey, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const sliced = rows.slice((page - 1) * PER, page * PER);
  const pages  = Math.ceil(rows.length / PER);

  // column sets per product
  const cols = svcKey === 'recharge' || svcKey === 'dth'
    ? ['#', 'Txn ID', 'API Txn ID', 'Number', 'Operator', 'Amount', 'Commission', 'Status', 'Date']
    : svcKey === 'bbps'
    ? ['#', 'Txn ID', 'Ref ID', 'Bill Account', 'Biller', 'Amount', 'Commission', 'Status', 'Date']
    : ['#', 'Txn ID', 'Ref ID', 'Account', 'Category', 'Amount', 'Status', 'Date'];

  const cell = (row, col, i) => {
    const idx = (page - 1) * PER + i + 1;
    if (col === '#')           return <td key={col} style={{ padding: '11px 14px', color: '#aaa', fontSize: 11, fontFamily: "'JetBrains Mono',monospace" }}>{idx}</td>;
    if (col === 'Status')      return <td key={col} style={{ padding: '11px 14px' }}><span style={{ ...statusStyle(row.status), borderRadius: 999, padding: '3px 10px', fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace" }}>{row.status}</span></td>;
    if (col === 'Amount')      return <td key={col} style={{ padding: '11px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11.5, fontWeight: 600, color: '#1a1a1a' }}>{fmt(row.amount)}</td>;
    if (col === 'Commission')  return <td key={col} style={{ padding: '11px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#0f6e56' }}>{fmt(row.commission ?? row['BC Commission'] ?? 0)}</td>;
    if (col === 'Date')        return <td key={col} style={{ padding: '11px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#888' }}>{row.created_at}</td>;
    if (col === 'Txn ID')      return <td key={col} style={{ padding: '11px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#534ab7' }}>{row.txn_id || '—'}</td>;
    if (col === 'API Txn ID')  return <td key={col} style={{ padding: '11px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#185fa5' }}>{row.api_txn_id || '—'}</td>;
    if (col === 'Ref ID')      return <td key={col} style={{ padding: '11px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#185fa5' }}>{row.ref_id || row.txn_id || '—'}</td>;
    if (col === 'Number')      return <td key={col} style={{ padding: '11px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{row.number || '—'}</td>;
    if (col === 'Operator')    return <td key={col} style={{ padding: '11px 14px', fontSize: 12 }}>{row.operator || '—'}</td>;
    if (col === 'Bill Account')return <td key={col} style={{ padding: '11px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{row.bill_account || row.number || '—'}</td>;
    if (col === 'Biller')      return <td key={col} style={{ padding: '11px 14px', fontSize: 12 }}>{row.biller || row.operator || '—'}</td>;
    if (col === 'Account')     return <td key={col} style={{ padding: '11px 14px', fontFamily: "'JetBrains Mono',monospace", fontSize: 11 }}>{row.number || '—'}</td>;
    if (col === 'Category')    return <td key={col} style={{ padding: '11px 14px', fontSize: 12 }}>{row.category || row.product || '—'}</td>;
    return <td key={col} style={{ padding: '11px 14px' }}>—</td>;
  };

  return (
    <div>
      {/* header bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 18px', background: `linear-gradient(90deg,${s.color}18,${s.color}08)`,
        borderBottom: `.5px solid ${s.color}33` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>{s.icon}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{s.label} — Transactions</div>
            <div style={{ fontSize: 11, color: '#888' }}>{rows.length} records{dateFrom ? ` · ${dateFrom} → ${dateTo}` : ' · today'}</div>
          </div>
        </div>
        <button className="ap-btn ap-btn-ghost ap-btn-sm" onClick={load}>↺ Refresh</button>
      </div>

      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#aaa', fontSize: 13 }}>
          <span className="ap-spin">⚙️</span> Loading transactions…
        </div>
      ) : rows.length === 0 ? (
        <div style={{ padding: '48px 0', textAlign: 'center', color: '#bbb', fontSize: 13 }}>
          No transactions found for this period.
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', maxHeight: 400 }}>
            <table className="ap-table">
              <thead><tr>{cols.map(c => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>{sliced.map((row, i) => <tr key={i}>{cols.map(c => cell(row, c, i))}</tr>)}</tbody>
            </table>
          </div>
          {pages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 18px', borderTop: '.5px solid rgba(0,0,0,.06)' }}>
              <span style={{ fontSize: 12, color: '#888' }}>Page {page} of {pages}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="ap-btn ap-btn-ghost ap-btn-sm" disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}>← Prev</button>
                <button className="ap-btn ap-btn-ghost ap-btn-sm" disabled={page === pages}
                  onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── API Key Panel ────────────────────────────────────────────────────────────
function ApiKeyPanel({ userid }) {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [visible,   setVisible]   = useState(false);
  const [cbUrl,     setCbUrl]     = useState('');
  const [generating,setGenerating]= useState(false);
  const [revoking,  setRevoking]  = useState(false);
  const [savingCb,  setSavingCb]  = useState(false);
  const [confirm,   setConfirm]   = useState(false);
  const [toast,     setToast]     = useState(null);

  const showToast = (msg, icon = '✅') => setToast({ msg, icon });

  const load = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('action', 'GET_API_KEY');
      fd.append('userid', userid);
      const r = await fetch(`${BASE}/api_partner_action`, { method: 'POST', headers: AUTH(), body: fd });
      const d = await r.json();
      if (d.status) { setData(d.data); setCbUrl(d.data?.callback_url || ''); }
    } catch(e) {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [userid]);

  const call = async (action, extra = {}) => {
    const fd = new FormData();
    fd.append('action', action); fd.append('userid', userid);
    Object.entries(extra).forEach(([k,v]) => fd.append(k, v));
    const r = await fetch(`${BASE}/api_partner_action`, { method: 'POST', headers: AUTH(), body: fd });
    return r.json();
  };

  const copy = (txt) => navigator.clipboard.writeText(txt).then(() => showToast('Copied to clipboard'));
  const masked = (k) => k ? k.slice(0,8)+'••••••••••••••••'+k.slice(-6) : '';
  const hasKey = !!(data?.api_key);

  const ENDPOINTS = [
    { method:'POST', path:'/create_mobile_recharge',          desc:'Mobile prepaid/postpaid recharge',       color:'#1d9e75', bg:'#e1f5ee' },
    { method:'POST', path:'/create_dth_recharge',      desc:'DTH / set-top-box recharge',             color:'#185fa5', bg:'#e6f1fb' },
    { method:'POST', path:'/pay_bbps1_bill',          desc:'Online bill payment via BBPS',            color:'#ba7517', bg:'#faeeda' },
    { method:'POST', path:'/pay_bbps1_bill_mannual',  desc:'Offline BBPS at merchant counter',       color:'#534ab7', bg:'#eeedfe' },
    { method:'GET',  path:'/fetch_bbps1_operator',      desc:'Fetch all BBPS biller categories',       color:'#639922', bg:'#eaf3de' },
    { method:'GET',  path:'/fetch_bbps1_bill',    desc:'Fetch operator plans for a number',      color:'#d85a30', bg:'#faece7' },
    { method:'GET',  path:'/bbps1_status_check',        desc:'Check transaction status by ref_id',     color:'#0c447c', bg:'#e6f1fb' },
  ];

  return (
    <div>
      {toast && <Toast msg={toast.msg} icon={toast.icon} onDone={() => setToast(null)} />}

      {/* Hero */}
      <div className="ap-fade" style={{ background: 'linear-gradient(135deg,#26215c,#3c3489 55%,#534ab7)',
        borderRadius: 18, padding: '26px 28px', marginBottom: 22, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -35, right: -35, width: 180, height: 180,
          borderRadius: '50%', background: 'rgba(255,255,255,.06)' }} />
        <div style={{ position: 'absolute', bottom: -25, left: 110, width: 120, height: 120,
          borderRadius: '50%', background: 'rgba(255,255,255,.04)' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <div style={{ width: 42, height: 42, borderRadius: 12,
              background: 'rgba(255,255,255,.18)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 22 }}>🔑</div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#fff' }}>API Credentials</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,.6)', marginTop: 2 }}>
                Manage your API key, webhook URL, and explore all endpoints
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Key Status',    value: loading ? '…' : hasKey ? '● Active' : '○ None' },
              { label: 'Rate Limit',    value: '1000 / hour' },
              { label: 'Auth Method',   value: 'Bearer Token' },
              { label: 'Webhook',       value: cbUrl ? '✓ Configured' : '— Not set' },
            ].map(s => (
              <div key={s.label} style={{ padding: '9px 16px', borderRadius: 10,
                background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.15)' }}>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,.5)', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 13,
                  color: '#afa9ec', fontWeight: 600 }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ '--accent': '#afa9ec' }} className="ap-shimmer" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        {/* LEFT: key management */}
        <div>
          <SH color="#534ab7" delay={0.05}>API Key Management</SH>
          <div className="ap-panel" style={{ padding: '20px 22px', marginBottom: 16 }}>
            {loading ? (
              <div style={{ display: 'flex', gap: 8, color: '#aaa', fontSize: 13 }}>
                <span className="ap-spin">⚙️</span> Loading…
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <div className={hasKey ? 'ap-dot-on' : 'ap-dot-off'} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: hasKey ? '#0f6e56' : '#a32d2d' }}>
                    {hasKey ? 'Active API Key' : 'No key — generate one to start'}
                  </span>
                  {data?.key_created_at && (
                    <span style={{ marginLeft: 'auto', fontSize: 10, color: '#aaa',
                      fontFamily: "'JetBrains Mono',monospace" }}>
                      Created {data.key_created_at}
                    </span>
                  )}
                </div>

                {hasKey && (
                  <div className="ap-key-box" style={{ marginBottom: 14 }}>
                    <span className="ap-key-text">
                      {visible ? data.api_key : masked(data.api_key)}
                    </span>
                    <button className="ap-btn ap-btn-ghost ap-btn-sm"
                      onClick={() => setVisible(v => !v)}>
                      {visible ? '🙈' : '👁'}
                    </button>
                    <button className="ap-btn ap-btn-ghost ap-btn-sm"
                      onClick={() => copy(data.api_key)}>📋</button>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="ap-btn ap-btn-violet" onClick={async () => {
                    setGenerating(true);
                    const d = await call('GENERATE_API_KEY');
                    if (d.status) { await load(); setVisible(true); showToast(d.data || 'Key generated'); }
                    else showToast(d.data || 'Failed', '❌');
                    setGenerating(false);
                  }} disabled={generating}>
                    {generating ? <><span className="ap-spin">⚙️</span> Generating…</> : hasKey ? '🔄 Regenerate' : '✨ Generate Key'}
                  </button>

                  {hasKey && !confirm && (
                    <button className="ap-btn ap-btn-red ap-btn-sm"
                      onClick={() => setConfirm(true)}>🗑 Revoke</button>
                  )}
                  {confirm && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                      padding: '7px 12px', background: '#fff3f3', borderRadius: 10,
                      border: '1.5px solid #f09595' }}>
                      <span style={{ fontSize: 11, color: '#a32d2d', fontWeight: 600 }}>
                        Breaks live integrations!
                      </span>
                      <button className="ap-btn ap-btn-red ap-btn-sm" disabled={revoking}
                        onClick={async () => {
                          setRevoking(true);
                          const d = await call('REVOKE_API_KEY');
                          if (d.status) { await load(); setVisible(false); showToast('Key revoked'); }
                          setConfirm(false); setRevoking(false);
                        }}>
                        {revoking ? <span className="ap-spin">⚙️</span> : 'Yes, Revoke'}
                      </button>
                      <button className="ap-btn ap-btn-ghost ap-btn-sm"
                        onClick={() => setConfirm(false)}>Cancel</button>
                    </div>
                  )}
                </div>

                {hasKey && (
                  <div style={{ marginTop: 12, padding: '9px 13px', background: '#fffbea',
                    borderRadius: 10, border: '1px solid #fac775', fontSize: 11, color: '#854f0b',
                    display: 'flex', gap: 7 }}>
                    ⚠️ Regenerating instantly invalidates the current key.
                  </div>
                )}
              </>
            )}
          </div>

          <SH color="#1d9e75" delay={0.10}>Callback / Webhook URL</SH>
          <div className="ap-panel" style={{ padding: '18px 20px' }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 12, lineHeight: 1.6 }}>
              We POST real-time transaction events here. Return HTTP 200 to acknowledge.
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input className="ap-input" type="url"
                placeholder="https://your-server.com/webhook"
                value={cbUrl} onChange={e => setCbUrl(e.target.value)} />
              <button className="ap-btn ap-btn-green" disabled={savingCb || !cbUrl.trim()}
                onClick={async () => {
                  setSavingCb(true);
                  const d = await call('UPDATE_CALLBACK', { callback_url: cbUrl.trim() });
                  showToast(d.status ? 'Saved' : (d.data || 'Failed'), d.status ? '✅' : '❌');
                  setSavingCb(false);
                }}>
                {savingCb ? <span className="ap-spin">⚙️</span> : '💾 Save'}
              </button>
              {cbUrl && <button className="ap-btn ap-btn-ghost ap-btn-sm"
                onClick={() => copy(cbUrl)}>📋</button>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {[
                ['📦', 'Payload', 'JSON (application/json)'],
                ['🔒', 'Signature', 'X-Signature: HMAC-SHA256'],
                ['🔁', 'Retries', '3× with exponential backoff'],
              ].map(([ic, lb, vl]) => (
                <div key={lb} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11 }}>
                  <span>{ic}</span>
                  <span style={{ color: '#aaa', minWidth: 65 }}>{lb}:</span>
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", color: '#1a1a1a',
                    fontWeight: 500 }}>{vl}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: auth + endpoints */}
        <div>
          <SH color="#ba7517" delay={0.05}>Authentication</SH>
          <div className="ap-panel" style={{ padding: '18px 20px', marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#555', marginBottom: 12, lineHeight: 1.6 }}>
              Include your API key in every request header:
            </div>
            <div className="ap-key-box" style={{ marginBottom: 12 }}>
              <span className="ap-key-text" style={{ fontSize: 12 }}>
                {`Authorization: Bearer ${hasKey && visible ? data.api_key : '<YOUR_API_KEY>'}`}
              </span>
              {hasKey && (
                <button className="ap-btn ap-btn-ghost ap-btn-sm"
                  onClick={() => copy(`Authorization: Bearer ${data.api_key}`)}>📋</button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                ['🌐', 'HTTPS Only',   'HTTP requests rejected'],
                ['⏱️', 'Rate Limit',   '1000 req/hour per key'],
                ['🔐', 'Keep Secret',  'Never expose in client code'],
                ['📬', 'Content-Type', 'application/json'],
              ].map(([ic, t, d]) => (
                <div key={t} style={{ padding: '11px 13px', background: '#f7f8fa',
                  borderRadius: 11, border: '1px solid rgba(0,0,0,.05)' }}>
                  <div style={{ fontSize: 15, marginBottom: 5 }}>{ic}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a1a', marginBottom: 3 }}>{t}</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{d}</div>
                </div>
              ))}
            </div>
          </div>

          <SH color="#185fa5" delay={0.10}>API Endpoints</SH>
          <div className="ap-panel" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', background: 'linear-gradient(90deg,#f7f8fa,#eef0f3)',
              borderBottom: '.5px solid rgba(0,0,0,.07)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12,
                color: '#534ab7', fontWeight: 700 }}>Base:</span>
              <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12 }}>{BASE}</span>
              <button style={{ marginLeft: 6, fontSize: 10, padding: '3px 10px', borderRadius: 6,
                border: '1px solid #534ab7', background: 'transparent', color: '#534ab7',
                cursor: 'pointer', fontWeight: 600 }} onClick={() => copy(BASE)}>Copy</button>
            </div>
            {ENDPOINTS.map((ep, i) => (
              <div key={i} className="ap-slide" style={{ display: 'flex', alignItems: 'flex-start',
                gap: 12, padding: '12px 16px',
                borderBottom: i < ENDPOINTS.length - 1 ? '.5px solid rgba(0,0,0,.05)' : 'none',
                animationDelay: `${.1 + i * .04}s` }}>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, fontWeight: 700,
                  padding: '3px 7px', borderRadius: 5, background: ep.bg, color: ep.color,
                  flexShrink: 0, marginTop: 2 }}>{ep.method}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12,
                    fontWeight: 500, color: '#1a1a1a', marginBottom: 2 }}>{ep.path}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{ep.desc}</div>
                </div>
                <button style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6,
                  border: `1px solid ${ep.color}44`, background: ep.bg, color: ep.color,
                  cursor: 'pointer', fontWeight: 600, flexShrink: 0 }}
                  onClick={() => copy(`${BASE}${ep.path}`)}>Copy</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ stats, currentTime }) {
  const svcs = Object.entries(SVC);
  const totSucc  = svcs.reduce((s, [k]) => s + (stats[k]?.success_count  || 0), 0);
  const totFail  = svcs.reduce((s, [k]) => s + (stats[k]?.failed_count   || 0), 0);
  const totAmt   = svcs.reduce((s, [k]) => s + (stats[k]?.success_amount || 0), 0);
  const totComm  = svcs.reduce((s, [k]) => s + (stats[k]?.commission     || 0), 0);
  const totTxn   = totSucc + totFail;
  const overallPct = totTxn ? ((totSucc / totTxn) * 100).toFixed(1) : '0.0';

  return (
    <div>
      {/* Summary strip */}
      <div className="ap-fade" style={{ display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: 12, marginBottom: 22 }}>
        {[
          { label: 'Wallet Balance',  value: fmt(stats.wallet ?? 0),  color: '#1d9e75', icon: '💼', sub: 'Available' },
          { label: "Today's Sales",   value: fmt(totAmt),              color: '#185fa5', icon: '💰', sub: 'Successful txns' },
          { label: "Today's Txns",    value: fmtN(totTxn),             color: '#534ab7', icon: '🔢', sub: `${totSucc} success · ${totFail} failed` },
          { label: 'Net Commission',  value: fmt(totComm),             color: '#ba7517', icon: '📈', sub: 'Earned today' },
          { label: 'Success Rate',    value: `${overallPct}%`,         color: '#639922', icon: '✅', sub: 'All services' },
        ].map((c, i) => (
          <div key={c.label} className="ap-fade" style={{ padding: '18px 20px', borderRadius: 16,
            background: `linear-gradient(135deg,${c.color}dd,${c.color})`,
            boxShadow: `0 4px 20px ${c.color}44`, position: 'relative', overflow: 'hidden',
            animationDelay: `${i * .06}s` }}>
            <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80,
              borderRadius: '50%', background: 'rgba(255,255,255,.1)' }} />
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.6)', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 18, fontWeight: 600,
              color: '#fff', marginBottom: 4 }}>{c.value}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.55)' }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Per-service breakdown */}
      <SH color="#534ab7">Service Breakdown — Today</SH>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 22 }}>
        {svcs.map(([k, s], i) => {
          const d    = stats[k] || {};
          const succ = d.success_count  || 0;
          const fail = d.failed_count   || 0;
          const amt  = d.success_amount || 0;
          const comm = d.commission     || 0;
          const total = succ + fail;
          const pct  = total ? ((succ / total) * 100).toFixed(1) : '0.0';
          return (
            <div key={k} className="ap-panel ap-fade"
              style={{ padding: '18px 20px', animationDelay: `${.1+i*.06}s` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: s.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                  {s.icon}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a1a' }}>{s.label}</div>
                  <div style={{ fontSize: 10, color: '#aaa' }}>{fmtN(total)} transactions</div>
                </div>
                <div style={{ marginLeft: 'auto', fontFamily: "'JetBrains Mono',monospace",
                  fontSize: 16, fontWeight: 700, color: s.color }}>{fmt(amt)}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                {[
                  { label: 'Success', value: fmtN(succ), color: '#0f6e56' },
                  { label: 'Failed',  value: fmtN(fail), color: '#a32d2d' },
                  { label: 'Comm.',   value: fmt(comm),  color: s.color   },
                ].map(x => (
                  <div key={x.label} style={{ padding: '9px 11px', background: '#f7f8fa',
                    borderRadius: 10, textAlign: 'center' }}>
                    <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 13,
                      fontWeight: 700, color: x.color }}>{x.value}</div>
                    <div style={{ fontSize: 9, color: '#aaa', marginTop: 3,
                      textTransform: 'uppercase', letterSpacing: '.06em' }}>{x.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: '#aaa' }}>Success rate</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: s.color }}>{pct}%</span>
              </div>
              <div className="ap-bar">
                <div className="ap-bar-fill" style={{ '--w': `${pct}%`, '--d': `${i*.1}s`, background: s.color }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Clock + quick links */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="ap-panel ap-fade" style={{ padding: '18px 20px', animationDelay: '.3s' }}>
          <div style={{ fontSize: 10, color: '#aaa', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '.1em', marginBottom: 10 }}>Live Clock</div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 24, fontWeight: 500,
            color: '#534ab7', letterSpacing: 1, marginBottom: 4 }}>
            {currentTime.toLocaleTimeString('en-IN')}
          </div>
          <div style={{ fontSize: 12, color: '#888' }}>
            {currentTime.toLocaleDateString('en-IN', { weekday:'long', day:'2-digit', month:'long', year:'numeric' })}
          </div>
        </div>
        <div className="ap-panel ap-fade" style={{ padding: '18px 20px', animationDelay: '.36s' }}>
          <div style={{ fontSize: 10, color: '#aaa', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '.1em', marginBottom: 12 }}>Quick Links</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { icon:'📄', label:'API Docs',    href:'/api_docs',          color:'#185fa5', bg:'#e6f1fb' },
              { icon:'💼', label:'Load Wallet', href:'/load_wallet',       color:'#1d9e75', bg:'#e1f5ee' },
              { icon:'🎫', label:'Support',     href:'/ticket_management', color:'#ba7517', bg:'#faeeda' },
              { icon:'📊', label:'Reports',     href:'/main_wallet_reports',  color:'#534ab7', bg:'#eeedfe' },
            ].map(lk => (
              <a key={lk.label} href={lk.href}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                  background: lk.bg, borderRadius: 11, textDecoration: 'none', border: `1px solid ${lk.color}22`,
                  transition: 'transform .15s' }}
                onMouseEnter={e => (e.currentTarget.style.transform = 'translateY(-1px)')}
                onMouseLeave={e => (e.currentTarget.style.transform = '')}>
                <span style={{ fontSize: 16 }}>{lk.icon}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: lk.color }}>{lk.label}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Wallet Tab ───────────────────────────────────────────────────────────────
function WalletTab({ stats }) {
  const wallet = stats.wallet ?? 0;
  return (
    <div>
      <SH color="#1d9e75">Wallet Balance</SH>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))',
        gap: 14, marginBottom: 22 }}>
        {[
          { label:'Main Wallet',   value: fmt(wallet),              color:'#1d9e75', grad:'linear-gradient(135deg,#0f6e56,#1d9e75)', icon:'💼' },
          { label:'Today Earned',  value: fmt(stats.commission??0), color:'#534ab7', grad:'linear-gradient(135deg,#3c3489,#534ab7)', icon:'📈' },
          { label:'Today Debited', value: fmt(stats.debited??0),    color:'#a32d2d', grad:'linear-gradient(135deg,#791f1f,#a32d2d)', icon:'📤' },
        ].map((c, i) => (
          <div key={c.label} className="ap-fade"
            style={{ padding: '20px', borderRadius: 18, background: c.grad, position: 'relative',
              overflow: 'hidden', boxShadow: `0 4px 20px ${c.color}44`, animationDelay: `${i*.07}s` }}>
            <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80,
              borderRadius: '50%', background: 'rgba(255,255,255,.1)' }} />
            <div style={{ fontSize: 22, marginBottom: 10 }}>{c.icon}</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,.6)', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6 }}>{c.label}</div>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 20, fontWeight: 600,
              color: '#fff' }}>{c.value}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <a href="/load_wallet" className="ap-btn ap-btn-green">💰 Add Fund / Request</a>
        <a href="/credit_debit_report" className="ap-btn ap-btn-ghost">📄 Credit / Debit Report</a>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function ApiPartnerDashboard() {
  const { userid } = useSession();

  const [dashData,    setDashData]    = useState(null);
  const [svcStats,    setSvcStats]    = useState({});
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [activeTab,   setActiveTab]   = useState('overview');
  const [activeSvc,   setActiveSvc]   = useState('recharge');  // which service card is selected in txns tab
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dateFrom,    setDateFrom]    = useState(new Date().toISOString().slice(0, 10));
  const [dateTo,      setDateTo]      = useState(new Date().toISOString().slice(0, 10));

  // Inject CSS
  useEffect(() => {
    const id = 'ap-styles';
    if (!document.getElementById(id)) {
      const el = document.createElement('style');
      el.id = id; el.textContent = CSS;
      document.head.appendChild(el);
    }
  }, []);

  // Clock
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Load dashboard stats — dedicated API Partner endpoint
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Calls the dedicated ApiPartnerDashboardAPI view
        const r = await fetchWrapper(`${BASE}/api_partner_dashboard_api`);
        setDashData(r);

        const stats = {};

        // Wallet balance
        stats.wallet = r.wallets?.wallet_amount ?? 0;

        // Mobile Recharge card
        const recharge = r.recharge || {};
        stats.recharge = {
          success_count:  recharge.success_count  ?? recharge.success?.count  ?? 0,
          failed_count:   recharge.failed_count   ?? recharge.failed?.count   ?? 0,
          success_amount: recharge.success_amount ?? recharge.success?.amount ?? 0,
          commission:     (r.services || []).find((s) => s.service === 'Mobile Recharge')?.net_commission ?? 0,
        };

        // DTH card
        const dth = r.dth || {};
        stats.dth = {
          success_count:  dth.success_count  ?? dth.success?.count  ?? 0,
          failed_count:   dth.failed_count   ?? dth.failed?.count   ?? 0,
          success_amount: dth.success_amount ?? dth.success?.amount ?? 0,
          commission:     (r.services || []).find((s) => s.service === 'DTH Recharge')?.net_commission ?? 0,
        };

        // BBPS Online card
        const bbpsOnline = r.bbps?.online || {};
        stats.bbps = {
          success_count:  bbpsOnline.success_count  ?? bbpsOnline.success?.count  ?? 0,
          failed_count:   bbpsOnline.failed_count   ?? bbpsOnline.failed?.count   ?? 0,
          success_amount: bbpsOnline.success_amount ?? bbpsOnline.success?.amount ?? 0,
          commission:     (r.services || []).find((s) => s.service === 'Bill Payments')?.net_commission ?? 0,
        };

        // Offline BBPS card
        const bbpsOffline = r.bbps?.offline || {};
        stats.offline = {
          success_count:  bbpsOffline.success_count  ?? bbpsOffline.success?.count  ?? 0,
          failed_count:   bbpsOffline.failed_count   ?? bbpsOffline.failed?.count   ?? 0,
          success_amount: bbpsOffline.success_amount ?? bbpsOffline.success?.amount ?? 0,
          commission:     (r.services || []).find((s) => s.service === 'Offline BBPS')?.net_commission ?? 0,
        };

        // Summary totals
        stats.commission = r.summary?.total_commission ?? 0;
        stats.debited    = 0;

        setSvcStats(stats);
      } catch(e) { setError(e.message); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const TABS = [
    { id: 'overview',    label: 'Overview',      icon: '📊' },
    { id: 'wallet',      label: 'Wallet',         icon: '💼' },
    { id: 'transactions',label: 'Transactions',   icon: '📋' },
    { id: 'api_access',  label: 'API Access',     icon: '🔑' },
  ];

  const TAB_COLORS = {
    overview:     'linear-gradient(135deg,#0f6e56,#1d9e75)',
    wallet:       'linear-gradient(135deg,#3c3489,#534ab7)',
    transactions: 'linear-gradient(135deg,#854f0b,#ba7517)',
    api_access:   'linear-gradient(135deg,#3c3489,#534ab7)',
  };

  if (loading) return (
    <div style={{ position: 'fixed', inset: 0, background: '#f5f6f8', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading…</span>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#a32d2d',
      fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      ⚠️ Failed to load dashboard: {error}
    </div>
  );

  return (
    <main className="ap-root">

      {/* ── Header ── */}
      <div className="ap-fade" style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 20, paddingBottom: 18,
        borderBottom: '1px solid rgba(0,0,0,.07)', position: 'relative', overflow: 'hidden' }}>
        {/* bg blobs */}
        <div style={{ position: 'absolute', width: 180, height: 180, borderRadius: '50%',
          background: '#534ab7', top: -80, left: -60, filter: 'blur(40px)', opacity: .18 }} />
        <div style={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%',
          background: '#1d9e75', top: -60, right: 80, filter: 'blur(40px)', opacity: .14 }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 13, position: 'relative' }}>
          <div style={{ width: 44, height: 44, borderRadius: 13,
            background: 'linear-gradient(135deg,#3c3489,#534ab7)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 22,
            boxShadow: '0 4px 16px rgba(83,74,183,.45)' }}>🔌</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', letterSpacing: '-.3px' }}>
              API Partner Dashboard
            </div>
            <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase',
              letterSpacing: '.1em', marginTop: 2 }}>
              Recharge · DTH · BBPS · Offline BBPS
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
          {/* Role badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 14px',
            borderRadius: 10, fontSize: 11, fontWeight: 700, letterSpacing: '.06em',
            textTransform: 'uppercase', border: '1.5px solid #534ab755',
            background: '#afa9ec22', color: '#534ab7' }}>
            <span style={{ fontSize: 14 }}>🔌</span> API Partner
          </div>
          {/* Live */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11,
            color: '#0f6e56', fontWeight: 600, background: '#e1f5ee',
            border: '1px solid #9fe1cb', borderRadius: 10, padding: '6px 14px' }}>
            <div className="ap-live" /> Live
          </div>
          {/* Date */}
          <div style={{ fontSize: 11, color: '#555', fontWeight: 500, background: '#fff',
            border: '1px solid rgba(0,0,0,.09)', borderRadius: 10, padding: '6px 14px',
            boxShadow: '0 2px 8px rgba(0,0,0,.05)' }}>
            {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </div>
        </div>
      </div>

      {/* ── Service Cards (always visible) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 22 }}>
        {Object.keys(SVC).map((k, i) => (
          <ServiceCard key={k} svcKey={k} data={svcStats[k]} delay={i * .07}
            active={activeTab === 'transactions' && activeSvc === k}
            onClick={() => { setActiveTab('transactions'); setActiveSvc(k); }} />
        ))}
      </div>

      {/* ── Tab Bar ── */}
      <div className="ap-tab-bar ap-fade" style={{ animationDelay: '.15s' }}>
        {TABS.map(t => (
          <button key={t.id}
            className={`ap-tab-btn${activeTab === t.id ? ' active' : ''}`}
            style={activeTab === t.id ? { background: TAB_COLORS[t.id] } : {}}
            onClick={() => setActiveTab(t.id)}>
            <span>{t.icon}</span>{t.label}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      <div key={activeTab} style={{ animation: 'apFadeUp .3s ease both' }}>

        {activeTab === 'overview' && (
          <OverviewTab stats={svcStats} currentTime={currentTime} />
        )}

        {activeTab === 'wallet' && (
          <WalletTab stats={svcStats} />
        )}

        {activeTab === 'transactions' && (
          <div>
            {/* date filter */}
            <div className="ap-panel ap-fade" style={{ padding: '14px 18px', marginBottom: 16,
              display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Date range:</span>
              <input className="ap-input" type="date" value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                style={{ width: 150, flex: 'none' }} />
              <span style={{ color: '#aaa', fontSize: 13 }}>→</span>
              <input className="ap-input" type="date" value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                style={{ width: 150, flex: 'none' }} />

              {/* service selector pills */}
              <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', flexWrap: 'wrap' }}>
                {Object.entries(SVC).map(([k, s]) => (
                  <button key={k}
                    onClick={() => setActiveSvc(k)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px',
                      borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: 11,
                      fontWeight: 700, fontFamily: 'inherit', transition: 'all .15s',
                      background: activeSvc === k ? s.color : '#f0f0f0',
                      color: activeSvc === k ? '#fff' : '#666',
                      boxShadow: activeSvc === k ? `0 3px 10px ${s.color}55` : 'none' }}>
                    <span>{s.icon}</span>{s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="ap-panel">
              <ReportsTable userid={userid} svcKey={activeSvc}
                dateFrom={dateFrom} dateTo={dateTo} />
            </div>
          </div>
        )}

        {activeTab === 'api_access' && (
          <ApiKeyPanel userid={userid} />
        )}

      </div>
    </main>
  );
}